const PROFESSIONAL_STATUS = {
    INACTIVE: 0,
    ACTIVE: 1,
    SUSPENDED: 2,
    DISABLED: 3,
};

const PROFESSIONAL_STATUS_PRESENTATIONS = {
    [PROFESSIONAL_STATUS.INACTIVE]: {
        description: "未生效",
        tagType: "",
        roleDescription: "该专业身份尚未生效。专业申请能力将在后续功能中提供。",
    },
    [PROFESSIONAL_STATUS.ACTIVE]: {
        description: "已认证",
        tagType: "success",
        roleDescription: "该专业身份的当前状态仅代表本专业，不影响另一专业入口。",
    },
    [PROFESSIONAL_STATUS.SUSPENDED]: {
        description: "实名认证撤销后暂停",
        tagType: "warning",
        roleDescription: "该专业身份的当前状态仅代表本专业，不影响另一专业入口。",
    },
    [PROFESSIONAL_STATUS.DISABLED]: {
        description: "后台停用",
        tagType: "info",
        roleDescription: "该专业身份的当前状态仅代表本专业，不影响另一专业入口。",
    },
};

const PROFESSIONAL_STATUS_DESCRIPTIONS = Object.keys(PROFESSIONAL_STATUS_PRESENTATIONS)
    .reduce((descriptions, status) => ({
        ...descriptions,
        [status]: PROFESSIONAL_STATUS_PRESENTATIONS[status].description,
    }), {});

const APPLICATION_TYPE_DESCRIPTIONS = {
    1: "首次申请",
    2: "风格审核申请",
    3: "设备审核申请",
    4: "证书审核申请",
    5: "本人真实照片审核（替换）",
    6: "本人真实照片审核（恢复）",
};

const APPLICATION_STATUS_DESCRIPTIONS = {
    1: "待审核",
    2: "已通过",
    3: "已驳回",
    4: "已撤回",
};

const ROLE_PORTRAIT_REVIEW_APPLICATION_TYPES = [5, 6];
const MATERIAL_REVIEW_APPLICATION_TYPES = [2, 3, 4];

const isRolePortraitReviewApplication = (applicationType) =>
    ROLE_PORTRAIT_REVIEW_APPLICATION_TYPES.indexOf(Number(applicationType)) >= 0;

const canWithdrawProfessionalMaterialUpdate = (application = {}) => {
    const candidate = application && typeof application === "object" ? application : {};
    return MATERIAL_REVIEW_APPLICATION_TYPES.indexOf(Number(candidate.applicationType)) >= 0 &&
        Number(candidate.applicationStatus) === 1;
};

const canShowProfessionalApplicationReviewReason = (applicationType, applicationStatus, reviewReason) =>
    Number(applicationStatus) === 3 &&
    (Number(applicationType) === 1 || isRolePortraitReviewApplication(applicationType)) &&
    !!asText(reviewReason);

const canReloadProfessionalApplicationDetail = ({ visible = false, applicationId = null, reloadAttempted = false } = {}) =>
    visible === true && !!applicationId && reloadAttempted !== true;

const asText = (value) => typeof value === "string" ? value.trim() : "";

const asList = (value) => Array.isArray(value) ? value : [];

const normalizeAttachment = (attachment = {}) => {
    if (!attachment || typeof attachment !== "object") return null;

    const accessUrl = asText(attachment.accessUrl);
    if (!accessUrl) return null;

    return {
        materialId: attachment.materialId || null,
        displayName: asText(attachment.displayName) || "附件",
        contentType: asText(attachment.contentType),
        accessUrl,
        expiresAt: asText(attachment.expiresAt),
    };
};

const normalizeAttachments = (attachments) => asList(attachments)
    .map(normalizeAttachment)
    .filter(Boolean);

const isProfessionalApplicationImageAttachment = (attachment = {}) =>
    !!(attachment && typeof attachment.contentType === "string" && attachment.contentType.indexOf("image/") === 0);

const getProfessionalApplicationDetailViewState = ({ loading = false, error = "", detail = null } = {}) => {
    if (loading) return "loading";
    if (asText(error)) return "error";
    return detail && typeof detail === "object" ? "content" : "empty";
};

const normalizeReviewItem = (item = {}) => ({
    operationType: Number(item.operationType) || null,
    operationTypeDescription: asText(item.operationTypeDescription),
    reviewStatus: Number(item.reviewStatus) || null,
    reviewStatusDescription: asText(item.reviewStatusDescription),
    reviewReason: asText(item.reviewReason),
});

const normalizeProfessionalApplicationDetail = (detail = {}, professionalType) => {
    const source = detail && typeof detail === "object" ? detail : {};
    const application = source.application && typeof source.application === "object" ? source.application : source;
    const applicationType = Number(application.applicationType) || null;
    const applicationStatus = Number(application.applicationStatus) || null;
    const isPortraitReviewApplication = isRolePortraitReviewApplication(applicationType);
    const canShowReviewReason = canShowProfessionalApplicationReviewReason(
        applicationType,
        applicationStatus,
        application.reviewReason,
    );
    const creator = source.creator && typeof source.creator === "object" ? source.creator : application.creator || {};
    const profile = source.profile && typeof source.profile === "object" ? source.profile : application.profile || {};
    const professional = source.professional && typeof source.professional === "object" ? source.professional : application.professional || {};
    const materials = source.materials && typeof source.materials === "object" ? source.materials : application.materials || source;
    const certificates = asList(materials.certificates).map((item) => ({
        ...normalizeReviewItem(item),
        certificateName: asText(item.certificateName),
        file: normalizeAttachment(item.file),
    }));
    const regionSource = profile.region && typeof profile.region === "object" ? profile.region : profile;
    const region = [regionSource.provinceName, regionSource.cityName, regionSource.districtName]
        .map(asText)
        .filter(Boolean)
        .join(" ");

    const normalizedDetail = {
        applicationId: application.applicationId || application.id || null,
        creatorName: asText(creator.creatorName) || asText(creator.realName) || asText(creator.name) || asText(application.creatorName),
        applicationType,
        applicationTypeDescription: asText(application.applicationTypeDescription),
        applicationStatus,
        applicationStatusDescription: asText(application.applicationStatusDescription),
        submittedAt: asText(application.submittedAt),
        finishedAt: asText(application.finishedAt),
        reviewReason: canShowReviewReason ? asText(application.reviewReason) : "",
        phone: asText(profile.phone),
        region,
        signedAgreement: normalizeAttachment(profile.signedAgreement),
        personalPhoto: isPortraitReviewApplication ? normalizeAttachment(source.personalPhoto) : null,
        certificates,
    };

    if (professionalType === "makeupArtist") {
        return {
            ...normalizedDetail,
            originalWorks: normalizeAttachments(materials.originalWorks),
        };
    }

    return {
        ...normalizedDetail,
        photographerIdentityStatus: Number(professional.identityStatus || professional.photographerIdentityStatus) || null,
        photographerIdentityStatusDescription: asText(professional.identityStatusDescription) || asText(professional.photographerIdentityStatusDescription),
        hasStyleOrderQualification: professional.hasStyleOrderQualification === true,
        styles: asList(materials.styles).map((item) => ({
            ...normalizeReviewItem(item),
            themeStyleId: item.themeStyleId === undefined || item.themeStyleId === null ? "" : String(item.themeStyleId),
            themeId: item.themeId === undefined || item.themeId === null ? "" : String(item.themeId),
            themeName: asText(item.themeName),
            styleId: item.styleId === undefined || item.styleId === null ? "" : String(item.styleId),
            styleName: asText(item.styleName),
            samples: normalizeAttachments(item.samples),
        })),
        equipments: asList(materials.equipments).map((item) => ({
            ...normalizeReviewItem(item),
            equipmentType: asText(item.equipmentType),
            brandModel: asText(item.brandModel),
            proofImages: normalizeAttachments(item.proofImages),
        })),
    };
};

const normalizeProfessionalProfile = (source = {}) => {
    const profile = source && typeof source.profile === "object" ? source.profile : source;
    const region = [profile.provinceName, profile.cityName, profile.districtName]
        .map(asText)
        .filter(Boolean)
        .join(" ");

    return {
        phone: asText(profile.phone) || asText(profile.verifiedPhone),
        provinceCode: asText(profile.provinceCode),
        cityCode: asText(profile.cityCode),
        districtCode: asText(profile.districtCode),
        provinceName: asText(profile.provinceName),
        cityName: asText(profile.cityName),
        districtName: asText(profile.districtName),
        region,
        professionalIntro: asText(profile.professionalIntro),
        signedAgreement: normalizeAttachment(profile.signedAgreement),
        personalPhoto: normalizeAttachment(profile.personalPhoto),
        personalPhotoApprovedAt: asText(profile.personalPhotoApprovedAt),
    };
};

const normalizePortraitSubmissionStatus = (source = {}) => {
    const status = source && typeof source === "object" ? source : {};
    return {
        canSubmit: status.canSubmit === true,
        blockReason: asText(status.blockReason),
        message: asText(status.message).replace(/角色真人形象照|真人形象照/g, "本人真实照片"),
        nextSubmitAt: asText(status.nextSubmitAt),
    };
};

const normalizeProfessionalRecovery = (source = {}) => {
    const recovery = source && typeof source === "object" ? source : {};
    const pendingRecoveryPortraitReview = recovery.pendingRecoveryPortraitReview === true;
    if (recovery.requiresRecovery !== true) {
        return pendingRecoveryPortraitReview
            ? { requiresRecovery: false, pendingRecoveryPortraitReview: true }
            : { requiresRecovery: false };
    }

    return {
        requiresRecovery: true,
        pendingRecoveryPortraitReview: false,
        realName: asText(recovery.realName),
        verifiedPhone: asText(recovery.verifiedPhone),
        provinceCode: asText(recovery.provinceCode),
        cityCode: asText(recovery.cityCode),
        districtCode: asText(recovery.districtCode),
        provinceName: asText(recovery.provinceName),
        cityName: asText(recovery.cityName),
        districtName: asText(recovery.districtName),
        professionalIntro: asText(recovery.professionalIntro),
    };
};

const getRequiredProfessionalRecoveries = (recoveries = {}) => [
    { professionalType: "photographer", recovery: normalizeProfessionalRecovery(recoveries.photographer) },
    { professionalType: "makeupArtist", recovery: normalizeProfessionalRecovery(recoveries.makeupArtist) },
].filter((item) => item.recovery.requiresRecovery);

const normalizeProfessionalCurrentMaterials = (data = {}, professionalType) => {
    const source = data && typeof data === "object" ? data : {};
    const certificates = asList(source.certificates).map((item) => ({
        id: item && item.id ? item.id : null,
        certificateName: asText(item && item.certificateName),
        file: normalizeAttachment(item && item.file),
    }));

    if (professionalType === "makeupArtist") {
        return {
            certificates,
            pendingReview: {
                certificate: source.pendingReview && source.pendingReview.certificate === true,
            },
        };
    }

    return {
        equipments: asList(source.equipments).map((item) => ({
            id: item && item.id ? item.id : null,
            equipmentType: asText(item && item.equipmentType),
            brandModel: asText(item && item.brandModel),
            proofImages: normalizeAttachments(item && item.proofImages),
        })),
        certificates,
        pendingReview: {
            equipment: source.pendingReview && source.pendingReview.equipment === true,
            certificate: source.pendingReview && source.pendingReview.certificate === true,
        },
    };
};

const normalizePhotographerCurrentStyles = (data = {}) => {
    const source = data && typeof data === "object" ? data : {};
    const normalizeStyle = (item = {}) => ({
        id: item.id || null,
        themeStyleId: item.themeStyleId === undefined || item.themeStyleId === null ? "" : String(item.themeStyleId),
        themeId: item.themeId === undefined || item.themeId === null ? "" : String(item.themeId),
        themeName: asText(item.themeName),
        styleId: item.styleId === undefined || item.styleId === null ? "" : String(item.styleId),
        styleName: asText(item.styleName),
    });
    return {
        styles: asList(source.styles).map(normalizeStyle),
        pendingStyles: asList(source.pendingStyles).map(normalizeStyle),
    };
};

const normalizeStatus = (value) => {
    const status = Number(value);
    return Object.prototype.hasOwnProperty.call(PROFESSIONAL_STATUS_PRESENTATIONS, status)
        ? status
        : PROFESSIONAL_STATUS.INACTIVE;
};

const normalizeApplication = (application = {}) => {
    const applicationType = Number(application.applicationType);
    const applicationStatus = Number(application.applicationStatus);

    return {
        id: application.id || null,
        applicationType,
        applicationTypeDescription: APPLICATION_TYPE_DESCRIPTIONS[applicationType] || "申请记录",
        applicationStatus,
        applicationStatusDescription: APPLICATION_STATUS_DESCRIPTIONS[applicationStatus] || "未知状态",
        submittedAt: asText(application.submittedAt),
        finishedAt: asText(application.finishedAt),
    };
};
const normalizeProfessionalApplicationList = (data = {}) => {
    const source = data && typeof data === "object" ? data : {};
    return {
        items: asList(source.items).map(normalizeApplication),
        total: Number(source.total) || 0,
        pageNo: Number(source.pageNo) || 1,
        pageSize: Number(source.pageSize) || 20,
    };
};

const normalizeProfessional = (professional = {}, defaults) => {
    const currentStatus = normalizeStatus(professional.currentStatus);
    const statusPresentation = PROFESSIONAL_STATUS_PRESENTATIONS[currentStatus];
    const initialApplication = professional.initialApplication
        ? normalizeApplication(professional.initialApplication)
        : null;

    return {
        professionalType: Number(professional.professionalType) || defaults.professionalType,
        professionalTypeDescription: asText(professional.professionalTypeDescription) || defaults.professionalTypeDescription,
        currentStatus,
        currentStatusDescription: statusPresentation.description,
        statusTagType: statusPresentation.tagType,
        statusDescription: statusPresentation.roleDescription,
        entryAction: asText(professional.entryAction),
        initialApplication,
    };
};

const normalizeCreatorOverview = (data = {}) => ({
    joined: data.joined === true,
    photographer: normalizeProfessional(data.photographer, {
        professionalType: 1,
        professionalTypeDescription: "摄影师",
    }),
    makeupArtist: normalizeProfessional(data.makeupArtist, {
        professionalType: 2,
        professionalTypeDescription: "化妆师",
    }),
});

const getCreatorAccessState = (error = {}) => {
    const response = error.response || {};
    const payload = response.data || error.data || {};
    const message = asText(payload.message) || "创作者中心加载失败，请稍后重试";

    const status = Number(payload.code || response.status || error.code);
    if (status === 403) {
        return {
            type: "certification",
            message,
            actionPath: "/center/basic",
        };
    }

    return {
        type: "error",
        message,
    };
};

module.exports = {
    APPLICATION_STATUS_DESCRIPTIONS,
    APPLICATION_TYPE_DESCRIPTIONS,
    MATERIAL_REVIEW_APPLICATION_TYPES,
    PROFESSIONAL_STATUS,
    PROFESSIONAL_STATUS_DESCRIPTIONS,
    PROFESSIONAL_STATUS_PRESENTATIONS,
    ROLE_PORTRAIT_REVIEW_APPLICATION_TYPES,
    canReloadProfessionalApplicationDetail,
    canWithdrawProfessionalMaterialUpdate,
    canShowProfessionalApplicationReviewReason,
    getCreatorAccessState,
    getProfessionalApplicationDetailViewState,
    isProfessionalApplicationImageAttachment,
    normalizeCreatorOverview,
    normalizeProfessionalApplicationList,
    normalizeProfessionalCurrentMaterials,
    normalizeProfessionalProfile,
    normalizePortraitSubmissionStatus,
    normalizeProfessionalRecovery,
    normalizeProfessionalApplicationDetail,
    normalizePhotographerCurrentStyles,
    isRolePortraitReviewApplication,
    getRequiredProfessionalRecoveries,
};
